import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, FileText, CreditCard, Tag, AlertTriangle, ShieldAlert, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { fixEncodingIssues } from '../../utils/textUtils';
import { propostaSaldoService, PropostaSaldoInfo, PropostaItemSaldo } from '../../services/propostaSaldoService';

interface PropostaDetailAccordionProps {
  proposta: any;
  total: number;
  saldoInfo?: PropostaSaldoInfo | null;
}

export const PropostaDetailAccordion: React.FC<PropostaDetailAccordionProps> = ({ proposta, total, saldoInfo: initialSaldoInfo }) => {
  const items = proposta.detalhes_do_pedido || [];
  const [saldoInfo, setSaldoInfo] = useState<PropostaSaldoInfo | null>(initialSaldoInfo || null);
  const [loadingSaldo, setLoadingSaldo] = useState(!initialSaldoInfo);

  useEffect(() => {
    if (initialSaldoInfo) {
      setSaldoInfo(initialSaldoInfo);
      setLoadingSaldo(false);
      return;
    }

    let isMounted = true;
    const loadSaldo = async () => {
      try {
        setLoadingSaldo(true);
        const info = await propostaSaldoService.getSaldoProposta(proposta.id);
        if (isMounted) {
          setSaldoInfo(info);
        }
      } catch (err) {
        console.warn('Erro ao obter saldo da proposta no accordion:', err);
      } finally {
        if (isMounted) setLoadingSaldo(false);
      }
    };

    loadSaldo();
    return () => {
      isMounted = false;
    };
  }, [proposta.id, initialSaldoInfo]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      const parts = dateStr.split('T')[0].split('-');
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (val?: number) => {
    return (val || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // Helper para renderizar o badge de alerta de saldo de um item
  const renderSaldoBadge = (itemSaldo?: PropostaItemSaldo) => {
    if (!itemSaldo || itemSaldo.quantidadeContratada === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600">
          Sob Demanda
        </span>
      );
    }

    switch (itemSaldo.nivelAlerta) {
      case 'esgotado_0':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800 border border-red-200">
            <ShieldAlert className="w-3 h-3 text-red-600" />
            Bloqueado (0 un)
          </span>
        );
      case 'urgente_1':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-300 animate-pulse">
            <AlertTriangle className="w-3 h-3 text-red-600" />
            Última Unidade (1 un)
          </span>
        );
      case 'critico_5':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-200">
            <AlertCircle className="w-3 h-3 text-orange-600" />
            Alerta Crítico ({itemSaldo.saldoRestante} un)
          </span>
        );
      case 'baixo_10':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            <AlertCircle className="w-3 h-3 text-amber-600" />
            Saldo Baixo ({itemSaldo.saldoRestante} un)
          </span>
        );
      case 'normal':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            Disponível ({itemSaldo.saldoRestante} un)
          </span>
        );
    }
  };

  return (
    <div className="p-3 sm:p-5 bg-slate-50 border-l-4 border-red-600 rounded-b-lg space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Proposta #{proposta.id}
          </span>
          <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-red-100 text-red-700">
            {proposta.status || 'Rascunho'}
          </span>
          {saldoInfo && saldoInfo.totalContratado > 0 && (
            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-800">
              Saldo Sistema: {saldoInfo.saldoRestante} de {saldoInfo.totalContratado} cotas disponíveis
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
          <span>Criado em: {formatDate(proposta.created_at)}</span>
        </div>
      </div>

      {/* Banner de Aviso/Alerta do Sistema sobre Saldo de Cotas */}
      {saldoInfo && saldoInfo.totalContratado > 0 && (
        <>
          {saldoInfo.nivelAlertaGeral === 'esgotado_0' && (
            <div className="p-3 bg-red-50 border border-red-300 rounded-xl flex items-start gap-2.5 text-xs text-red-900 shadow-xs">
              <ShieldAlert className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold uppercase tracking-wider text-[11px] block">
                  ⛔ Bloqueio Operacional: Cota Totalmente Esgotada
                </strong>
                <span>
                  Todas as {saldoInfo.totalContratado} unidades contratadas nesta proposta já foram emitidas em Ordens de Serviço (Saldo: 0).
                  Novas solicitações para este contrato estão bloqueadas até aditivo ou renovação comercial.
                </span>
              </div>
            </div>
          )}

          {saldoInfo.nivelAlertaGeral === 'urgente_1' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2.5 text-xs text-red-900 shadow-xs">
              <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold uppercase tracking-wider text-[11px] block">
                  ⚠️ Aviso Urgente ao Cliente: Resta Apenas 1 Unidade
                </strong>
                <span>
                  Atenção! Resta apenas 1 viagem/serviço disponível nesta proposta. Alinhe previamente com o cliente a emissão da última OS e a renovação do pacote.
                </span>
              </div>
            </div>
          )}

          {saldoInfo.nivelAlertaGeral === 'critico_5' && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl flex items-start gap-2.5 text-xs text-orange-900 shadow-xs">
              <AlertCircle className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold uppercase tracking-wider text-[11px] block">
                  🚨 Alerta Crítico ao Cliente: Cota Quase Esgotada ({saldoInfo.saldoRestante} unidades restantes)
                </strong>
                <span>
                  {saldoInfo.alertaMensagemGeral} Notifique o cliente e a equipe comercial para aprovar novo aditivo antes que a cota seja zerada.
                </span>
              </div>
            </div>
          )}

          {saldoInfo.nivelAlertaGeral === 'baixo_10' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 text-xs text-amber-900 shadow-xs">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold uppercase tracking-wider text-[11px] block">
                  ⚠️ Aviso de Saldo Baixo ao Cliente ({saldoInfo.saldoRestante} unidades restantes)
                </strong>
                <span>
                  {saldoInfo.alertaMensagemGeral} Acompanhe o consumo para sugerir reposição ao cliente no momento oportuno.
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Itens / Serviços do Pedido */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mb-2.5">
          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 text-red-600 shrink-0" /> Itens / Serviços ({items.length})
          </h4>
          <span className="text-[10px] font-semibold text-slate-500 bg-slate-200 px-2 py-0.5 rounded w-fit">
            Colunas de Saldo visíveis apenas em sistema
          </span>
        </div>

        {items.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Nenhum serviço detalhado nesta proposta.</p>
        ) : (
          <>
            {/* Visualização Desktop (Tabela Completa com Scroll) */}
            <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs min-w-[680px]">
                  <thead className="bg-slate-100 text-slate-600 font-semibold border-b border-slate-200">
                    <tr>
                      <th className="p-2.5">Descrição do Serviço</th>
                      <th className="p-2.5 text-center bg-blue-50/50 text-blue-900 border-l border-blue-100" title="Quantidade originalmente contratada nesta proposta">
                        Contratado
                      </th>
                      <th className="p-2.5 text-center bg-blue-50/50 text-blue-900" title="Quantidade já utilizada em Ordens de Serviço emitidas">
                        Utilizado (OS)
                      </th>
                      <th className="p-2.5 text-center bg-blue-50/50 text-blue-900" title="Saldo restante disponível no sistema">
                        Saldo Restante
                      </th>
                      <th className="p-2.5 text-center bg-blue-50/50 text-blue-900 border-r border-blue-100">
                        Status da Cota
                      </th>
                      <th className="p-2.5 text-right">Valor Unitário</th>
                      <th className="p-2.5 text-right">Valor Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map((item: any, idx: number) => {
                      const qtd = Number(item.quantidade) || 0;
                      const unit = Number(item.valor_unitario) || 0;
                      const itemTotal = Number(item.valor_total) || (qtd * unit);
                      const servicoTexto = fixEncodingIssues(item.servicos) || 'Serviço não especificado';

                      const itemSaldo = saldoInfo?.itens?.find(
                        (it) => String(it.id) === String(item.id) || it.servicos.toLowerCase().trim() === servicoTexto.toLowerCase().trim()
                      );

                      const contratado = itemSaldo ? itemSaldo.quantidadeContratada : qtd;
                      const utilizado = itemSaldo ? itemSaldo.quantidadeUtilizada : 0;
                      const saldoRest = itemSaldo ? itemSaldo.saldoRestante : qtd;
                      const isBloqueado = itemSaldo?.nivelAlerta === 'esgotado_0';

                      return (
                        <tr key={item.id || idx} className={`hover:bg-slate-50 transition-colors ${isBloqueado ? 'bg-red-50/40' : ''}`}>
                          <td className="p-2.5 font-medium text-slate-800">
                            {servicoTexto}
                          </td>
                          <td className="p-2.5 text-center font-semibold text-slate-700 bg-blue-50/20 border-l border-blue-50">
                            {contratado > 0 ? contratado : '—'}
                          </td>
                          <td className="p-2.5 text-center font-medium text-slate-600 bg-blue-50/20">
                            {contratado > 0 ? utilizado : '—'}
                          </td>
                          <td className={`p-2.5 text-center font-bold bg-blue-50/20 ${
                            saldoRest <= 0 && contratado > 0 ? 'text-red-700' :
                            saldoRest === 1 ? 'text-red-600' :
                            saldoRest <= 5 && contratado > 15 ? 'text-orange-600' :
                            'text-slate-900'
                          }`}>
                            {contratado > 0 ? saldoRest : '—'}
                          </td>
                          <td className="p-2.5 text-center bg-blue-50/20 border-r border-blue-50">
                            {renderSaldoBadge(itemSaldo)}
                          </td>
                          <td className="p-2.5 text-right text-slate-600">{formatCurrency(unit)}</td>
                          <td className="p-2.5 text-right font-semibold text-slate-900">{formatCurrency(itemTotal)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t border-slate-200 font-bold text-xs">
                    <tr>
                      <td className="p-2.5 text-slate-700 uppercase">Totais da Proposta:</td>
                      <td className="p-2.5 text-center text-blue-900 bg-blue-50/40 border-l border-blue-100">
                        {saldoInfo?.totalContratado || 0}
                      </td>
                      <td className="p-2.5 text-center text-blue-900 bg-blue-50/40">
                        {saldoInfo?.totalUtilizado || 0}
                      </td>
                      <td className="p-2.5 text-center text-blue-900 bg-blue-50/40 font-extrabold">
                        {saldoInfo?.saldoRestante ?? 0}
                      </td>
                      <td className="p-2.5 text-center bg-blue-50/40 border-r border-blue-100 text-[10px] uppercase font-bold text-slate-500">
                        {saldoInfo?.alertaTituloGeral || 'Em Sistema'}
                      </td>
                      <td className="p-2.5 text-right text-slate-700 uppercase">Total Geral:</td>
                      <td className="p-2.5 text-right text-red-600 font-extrabold text-sm">{formatCurrency(total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Visualização Mobile (Cards Responsivos Elegantes) */}
            <div className="block md:hidden space-y-3">
              {items.map((item: any, idx: number) => {
                const qtd = Number(item.quantidade) || 0;
                const unit = Number(item.valor_unitario) || 0;
                const itemTotal = Number(item.valor_total) || (qtd * unit);
                const servicoTexto = fixEncodingIssues(item.servicos) || 'Serviço não especificado';

                const itemSaldo = saldoInfo?.itens?.find(
                  (it) => String(it.id) === String(item.id) || it.servicos.toLowerCase().trim() === servicoTexto.toLowerCase().trim()
                );

                const contratado = itemSaldo ? itemSaldo.quantidadeContratada : qtd;
                const utilizado = itemSaldo ? itemSaldo.quantidadeUtilizada : 0;
                const saldoRest = itemSaldo ? itemSaldo.saldoRestante : qtd;
                const isBloqueado = itemSaldo?.nivelAlerta === 'esgotado_0';

                return (
                  <div 
                    key={item.id || idx} 
                    className={`bg-white p-3.5 rounded-xl border ${isBloqueado ? 'border-red-300 bg-red-50/30' : 'border-slate-200'} shadow-2xs space-y-2.5`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold text-slate-900 text-xs leading-snug">
                        {servicoTexto}
                      </div>
                      <div className="shrink-0">
                        {renderSaldoBadge(itemSaldo)}
                      </div>
                    </div>

                    {/* Grade de Cotas */}
                    <div className="grid grid-cols-3 gap-2 bg-slate-50 p-2.5 rounded-lg text-center border border-slate-100">
                      <div>
                        <span className="text-[10px] text-slate-500 font-medium block">Contratado</span>
                        <span className="text-xs font-bold text-slate-800">{contratado > 0 ? contratado : '—'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-500 font-medium block">Utilizado (OS)</span>
                        <span className="text-xs font-bold text-slate-600">{contratado > 0 ? utilizado : '—'}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-500 font-medium block">Saldo Restante</span>
                        <span className={`text-xs font-extrabold ${
                          saldoRest <= 0 && contratado > 0 ? 'text-red-700' :
                          saldoRest === 1 ? 'text-red-600' :
                          saldoRest <= 5 && contratado > 15 ? 'text-orange-600' :
                          'text-emerald-700'
                        }`}>
                          {contratado > 0 ? saldoRest : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Valores */}
                    <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                      <span className="text-slate-500">
                        Unitário: <strong className="text-slate-700 font-medium">{formatCurrency(unit)}</strong>
                      </span>
                      <span className="text-slate-900 font-bold">
                        Total: <span className="text-red-600">{formatCurrency(itemTotal)}</span>
                      </span>
                    </div>
                  </div>
                );
              })}

              {/* Card de Totais Mobile */}
              <div className="bg-slate-900 text-white p-3.5 rounded-xl shadow-sm space-y-2">
                <div className="flex items-center justify-between text-xs border-b border-slate-800 pb-2">
                  <span className="text-slate-400 font-medium">Total de Cotas:</span>
                  <span className="font-bold">{saldoInfo?.totalContratado || 0} contratadas • {saldoInfo?.saldoRestante ?? 0} disponíveis</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-300 font-semibold uppercase text-[11px] tracking-wider">Total da Proposta:</span>
                  <span className="text-base font-extrabold text-red-400">{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Informações Complementares */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-[11px] font-bold text-slate-500 uppercase flex items-center gap-1 mb-1.5">
            <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" /> Considerações
          </span>
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
            {fixEncodingIssues(proposta.consideracoes) || 'Sem considerações registradas.'}
          </p>
        </div>

        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-[11px] font-bold text-slate-500 uppercase flex items-center gap-1 mb-1.5">
            <DollarSign className="w-3.5 h-3.5 text-slate-400 shrink-0" /> Condições Comerciais
          </span>
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
            {fixEncodingIssues(proposta.condicoes_comerciais) || 'Sem condições comerciais registradas.'}
          </p>
        </div>

        <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs">
          <span className="text-[11px] font-bold text-slate-500 uppercase flex items-center gap-1 mb-1.5">
            <CreditCard className="w-3.5 h-3.5 text-slate-400 shrink-0" /> Condições de Pagamento
          </span>
          <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
            {fixEncodingIssues(proposta.condicoes_pagamento) || 'Sem condições de pagamento registradas.'}
          </p>
        </div>
      </div>
    </div>
  );
};
